import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {EditableTextfieldContent} from 'shells/editable-textfield-content/editable-textfield-content';
import {NotePencilRegularIcon} from 'shells/icon/svg/note-pencil-regular-icon';
import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {PencilOutlineIcon} from 'shells/icon/svg/pencil-outline-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {BaseRow} from 'shells/row/base-row/base-row';
import {Body} from 'shells/typography/body';

import type {
    GetProductsForLead_lead_products_edges as LeadProductMap,
    WidgetFragment as Widget,
} from 'nutshell-graphql-types';

import {CardWidgetWrapper} from '../../../core/card';
import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../core/card/helpers';

import {refetchForEvent} from '../../refetch-helpers';

import {MarketSelector} from '../revenue-widget/components/market-selector';
import {ProductsRowCreateForm} from '../revenue-widget/components/products-row-create-form';
import {useAddProductForLead} from '../revenue-widget/graphql/hooks/use-add-product-for-lead';
import {useGetMarketForLead} from '../revenue-widget/graphql/hooks/use-get-market-for-lead';
import {useGetProductsForLead} from '../revenue-widget/graphql/hooks/use-get-products-for-lead';
import {useRemoveProductForLead} from '../revenue-widget/graphql/hooks/use-remove-product-for-lead';
import {useUpdateProductForLead} from '../revenue-widget/graphql/hooks/use-update-product-for-lead';

import {ProductPopover} from './components/product-popover';

type Props = {
    leadId: string;
    widget?: Widget;
};

export function ProductWidgetWrapper(props: Props) {
    const [isAddingProduct, setIsAddingProduct] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null | undefined>(null);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = React.useState<LeadProductMap | null | undefined>(
        null
    );
    const [productNoteToEdit, setProductNoteToEdit] = React.useState<
        LeadProductMap | null | undefined
    >(null);
    const [removeConfirmationForProductId, setRemoveConfirmationForProductId] = React.useState<
        string | null | undefined
    >(null);

    const popoverRef = React.useRef<HTMLElement | null | undefined>(null);
    const rowRefs = React.useRef<{
        [id: string]: {
            current: HTMLElement | null | undefined;
        };
    }>({});
    const removeButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const {market} = useGetMarketForLead(props.leadId);
    const {products, isLoading} = useGetProductsForLead(props.leadId);

    const {addProductForLead} = useAddProductForLead();
    const {updateProductForLead} = useUpdateProductForLead();
    const {removeProductForLead} = useRemoveProductForLead();

    const handleRemoveProduct = React.useCallback(
        (product: LeadProductMap) => {
            removeProductForLead(product)
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                })
                .then(() => {
                    refetchForEvent('productsUpdated');
                });
        },
        [removeProductForLead]
    );

    const handleUpdateProductNote = React.useCallback(
        (product: LeadProductMap, note: string) => {
            updateProductForLead({
                leadProductMap: product.id,
                note: note,
            })
                .then(() => {
                    setProductNoteToEdit(null);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [updateProductForLead]
    );

    const handleSelect = React.useCallback(
        (product: {value: string}) => {
            addProductForLead({
                lead: props.leadId,
                product: product.value,
            })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                })
                .then(() => {
                    refetchForEvent('productsUpdated');
                })
                .finally(() => {
                    setIsAddingProduct(false);
                });
        },
        [addProductForLead, props.leadId]
    );

    const getProductRowRef = React.useCallback((productId: string) => {
        if (!rowRefs.current[productId]) {
            rowRefs.current[productId] = {current: null};
        }

        return rowRefs.current[productId];
    }, []);

    const setProductPopover = React.useCallback(
        (product: LeadProductMap) => {
            const rowRef = rowRefs.current[product.id];
            popoverRef.current = rowRef && rowRef.current;
            setSelectedProduct(product);
            setIsPopoverOpen(true);
        },
        [setSelectedProduct, setIsPopoverOpen]
    );

    const count = products ? products.edges.length : 0;
    const isEmpty = count === 0 && !isAddingProduct;

    const content = (
        <StackLegacy spacing={8}>
            {isAddingProduct && market != null && (
                <ProductsRowCreateForm
                    currentMarket={market}
                    onSelect={handleSelect}
                    onCancel={() => setIsAddingProduct(false)}
                />
            )}
            {products &&
                products.edges.map((product: LeadProductMap) => {
                    const toolbarOptions = [
                        {
                            id: 'product-note',
                            tooltipText: product.relationshipNote ? 'Edit note' : 'Add note',
                            icon: product.relationshipNote
                                ? NotePencilRegularIcon
                                : NoteRegularIcon,
                            onClick: () => {
                                setProductNoteToEdit(product);
                            },
                        },
                        {
                            id: 'product-edit',
                            tooltipText: 'Edit product',
                            icon: PencilOutlineIcon,
                            onClick: () => {
                                setProductPopover(product);
                            },
                        },
                        {
                            id: 'product-remove',
                            tooltipText: 'Remove product',
                            icon: TrashRegularIcon,
                            onClick: () => {
                                setRemoveConfirmationForProductId(product.id);
                            },
                            // @ts-expect-error
                            getRef: (ref) => {
                                removeButtonRef.current = ref;
                            },
                        },
                    ];

                    const isEditingNote = Boolean(
                        productNoteToEdit && productNoteToEdit.id === product.id
                    );

                    return (
                        <>
                            <BaseRow
                                key={product.id}
                                title={product.node.name}
                                toolbarProps={{
                                    options: toolbarOptions,
                                    forceHideToolbar: isEditingNote,
                                    forceShowToolbar: removeConfirmationForProductId === product.id,
                                }}
                                type='products'
                                getRowRef={getProductRowRef(product.id)}
                                onClick={() => {
                                    setProductPopover(product);
                                }}
                            >
                                <>
                                    {product.node.description && (
                                        <Body
                                            size='small'
                                            as='div'
                                            color='light'
                                            truncate={{type: 'block', maxLines: 2}}
                                        >
                                            {product.node.description}
                                        </Body>
                                    )}
                                    <Body size='small' as='div'>
                                        {product.price.formattedWithCents}{' '}
                                        {product.quantity > 1 && ` • Qty ${product.quantity}`}
                                    </Body>
                                    {(product.relationshipNote || isEditingNote) && (
                                        <EditableTextfieldContent
                                            value={product.relationshipNote}
                                            onUpdate={(note) =>
                                                handleUpdateProductNote(product, note || '')
                                            }
                                            isMultiline={true}
                                            isEditing={isEditingNote}
                                            setIsEditing={(isEditing) => {
                                                if (isEditing) {
                                                    setProductNoteToEdit(product);
                                                } else {
                                                    setProductNoteToEdit(null);
                                                }
                                            }}
                                        />
                                    )}
                                </>
                            </BaseRow>
                            {removeConfirmationForProductId === product.id && (
                                <CoreEntityConfirmationDialog
                                    anchor={removeButtonRef.current}
                                    type='product'
                                    parentEntity='lead'
                                    onConfirm={() => {
                                        setRemoveConfirmationForProductId(null);
                                        handleRemoveProduct(product);
                                    }}
                                    onCancel={() => {
                                        setRemoveConfirmationForProductId(null);
                                    }}
                                />
                            )}
                        </>
                    );
                })}
        </StackLegacy>
    );

    const marketSelector = <MarketSelector leadId={props.leadId} setError={setError} />;

    return (
        <>
            {NutClientConfig.hasLeadPageRedesignM3FF ? (
                props.widget && (
                    <CollapsibleCardWidget
                        widget={props.widget}
                        secondaryTitle={marketSelector}
                        addButtonProps={{
                            onClick: () => setIsAddingProduct(true),
                            isDisabled: isAddingProduct,
                            variant: 'singular',
                        }}
                        emptyStateProps={getWidgetEmptyStateProps('leadProducts', isEmpty)}
                        isLoading={isLoading}
                        errorProps={{
                            error: error,
                            setError: setError,
                        }}
                        count={count}
                        setIsAdding={(isAdding) => {
                            setIsAddingProduct(isAdding);
                        }}
                    >
                        {content}
                    </CollapsibleCardWidget>
                )
            ) : (
                <CardWidgetWrapper
                    widgetKey='leadProducts'
                    secondaryTitle={marketSelector}
                    count={count}
                    addButtonProps={{
                        onClick: () => setIsAddingProduct(true),
                        isDisabled: isAddingProduct,
                        variant: 'singular',
                    }}
                    isEmpty={isEmpty}
                    isLoading={isLoading}
                    errorProps={{
                        error: error,
                        setError: setError,
                    }}
                >
                    {content}
                </CardWidgetWrapper>
            )}
            {isPopoverOpen && selectedProduct && popoverRef.current && (
                <ProductPopover
                    anchor={popoverRef.current}
                    product={selectedProduct}
                    onClose={() => setIsPopoverOpen(false)}
                />
            )}
        </>
    );
}
